import axios from "axios";
import store from "../store/store";
import { logout } from "../features/auth/user.reducer";
import {
  buildAuthHeaders,
  getStoredJwtToken,
  normalizeRawToken,
} from "./authHeaders";

const getHeaderValue = (headers, key) => {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    return headers.get(key) || headers.get(key.toLowerCase()) || "";
  }

  const matchingKey = Object.keys(headers).find(
    (headerKey) => headerKey.toLowerCase() === key.toLowerCase()
  );
  return matchingKey ? headers[matchingKey] : "";
};

const SESSION_HEADER_NAMES = new Set(["jwt", "x-jwt-token"]);

const toPlainHeaders = (headers) => {
  if (!headers) return {};
  if (typeof headers.toJSON === "function") {
    return headers.toJSON();
  }
  return { ...headers };
};

export const getRequestJwtToken = (config = {}) => {
  const headers = config?.headers;
  const requestToken =
    getHeaderValue(headers, "X-JWT-TOKEN") || getHeaderValue(headers, "jwt");
  return normalizeRawToken(String(requestToken || ""));
};

export const mergeAuthHeaders = (headers = {}, jwtToken = "") => {
  const plainHeaders = toPlainHeaders(headers);
  const sanitizedHeaders = Object.keys(plainHeaders).reduce((acc, key) => {
    if (!SESSION_HEADER_NAMES.has(key.toLowerCase())) {
      acc[key] = plainHeaders[key];
    }
    return acc;
  }, {});

  return {
    ...sanitizedHeaders,
    ...buildAuthHeaders(jwtToken || getStoredJwtToken()),
  };
};

export const shouldLogoutForAuthFailure = (config = {}, activeToken = "") => {
  const currentToken = normalizeRawToken(activeToken || getStoredJwtToken());
  const requestToken = getRequestJwtToken(config);

  if (!currentToken || !requestToken) return false;
  return currentToken === requestToken;
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 10000,
  timeoutErrorMessage:
    "Request taking longer than expected. Check your network",
  headers: {
    "Content-Type": "application/json",
    ...buildAuthHeaders(),
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getStoredJwtToken();
    config.headers = mergeAuthHeaders(config.headers, token);

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data?.status === "Fail") {
      const statusCode = String(response?.data?.status_code ?? "");
      const message = String(response?.data?.message ?? "").toLowerCase();
      if (
        (statusCode === "304" ||
          message.includes("jwt token is not valid")) &&
        shouldLogoutForAuthFailure(response.config)
      ) {
        store.dispatch(logout());
      }

      const error = new Error();
      error.response = {
        ...response,
        data: response.data,
        status: 400,
      };
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    if (
      error.response?.status === 401 &&
      shouldLogoutForAuthFailure(error.config)
    ) {
      store.dispatch(logout());
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
